import React, { useState, useEffect } from 'react';
import { Toaster } from 'sonner';
import AdminLogin from './AdminLogin';
import AdminDashboard from './AdminDashboard';
import AdminSubmissionView from './AdminSubmissionView';
import AdminAudioDashboard from './AdminAudioDashboard';
import SolutgenSupportWidget from '../components/SolutgenSupportWidget';

function parseHash() {
  if (window.location.pathname.startsWith('/admin/audio')) {
    return { view: 'audio', id: null };
  }
  const hash = (window.location.hash || '').replace('#', '');
  if (hash === '/audio') return { view: 'audio', id: null };
  const match = hash.match(/^\/submission\/(.+)$/);
  if (match) return { view: 'submission', id: match[1] };
  return { view: 'dashboard', id: null };
}

export function AdminApp() {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);

  // Parse hash on mount and on hash change
  useEffect(() => {
    const handleHash = () => {
      const { view, id } = parseHash();
      if (view === 'submission' && id) {
        setCurrentView('submission');
        setSelectedSubmissionId(id);
      } else if (view === 'audio') {
        setCurrentView('audio');
        setSelectedSubmissionId(null);
      } else {
        setCurrentView('dashboard');
        setSelectedSubmissionId(null);
      }
    };

    handleHash(); // parse on mount
    window.addEventListener('hashchange', handleHash);
    return () => window.removeEventListener('hashchange', handleHash);
  }, []);

  useEffect(() => {
    // Check for existing token
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      verifyToken(savedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
      const res = await fetch(`${API_BASE}/api/admin/verify`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${tokenToVerify}` }
      });

      if (res.ok) {
        const data = await res.json();
        setToken(tokenToVerify);
        const verifiedUser = data.user || JSON.parse(localStorage.getItem('admin_user') || 'null') || { role: 'admin' };
        setUser(verifiedUser);
        if (verifiedUser.role === 'editor') {
          setCurrentView('audio');
          setSelectedSubmissionId(null);
        }
      } else {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
      }
    } catch (error) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = (newToken, loggedUser) => {
    setToken(newToken);
    setUser(loggedUser || { role: 'admin' });
    if (loggedUser?.role === 'editor') {
      setCurrentView('audio');
      setSelectedSubmissionId(null);
      window.history.pushState(null, '', '/admin/audio');
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    setCurrentView('dashboard');
    setSelectedSubmissionId(null);
    localStorage.removeItem('admin_user');
    window.location.hash = '';
  };

  const handleViewSubmission = (submissionId) => {
    setSelectedSubmissionId(submissionId);
    setCurrentView('submission');
    window.location.hash = `/submission/${submissionId}`;
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setSelectedSubmissionId(null);
    window.history.pushState(null, '', '/admin');
    window.location.hash = '';
  };

  const handleOpenAudio = () => {
    setCurrentView('audio');
    setSelectedSubmissionId(null);
    window.history.pushState(null, '', '/admin/audio');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  return (
    <>
      <Toaster richColors position="top-center" />
      <SolutgenSupportWidget
        enabled={Boolean(token && user)}
        user={{
          id: user?.id || user?.username || user?.email || user?.role || 'admin',
          name: user?.name || user?.username || (user?.role === 'editor' ? 'Editor' : 'Administración'),
          email: user?.email || '',
          role: user?.role || 'admin'
        }}
        context={{
          module: window.location.pathname,
          adminView: currentView,
          submissionId: selectedSubmissionId || ''
        }}
      />
      {!token ? (
        <AdminLogin onLogin={handleLogin} />
      ) : currentView === 'audio' || user?.role === 'editor' ? (
        <AdminAudioDashboard
          token={token}
          user={user}
          onLogout={handleLogout}
          onBackToAdmin={user?.role === 'admin' ? handleBackToDashboard : undefined}
        />
      ) : currentView === 'submission' && selectedSubmissionId ? (
        <AdminSubmissionView
          submissionId={selectedSubmissionId}
          token={token}
          onBack={handleBackToDashboard}
        />
      ) : (
        <AdminDashboard
          token={token}
          user={user}
          onLogout={handleLogout}
          onViewSubmission={handleViewSubmission}
          onOpenAudio={handleOpenAudio}
        />
      )}
    </>
  );
}

export default AdminApp;
